/**
 * Helper: General
 * -----------------------------------------------------------------------------
 * General helper utility functions.
 * - Functions which don't fit into any of the other utils files.
 * - Add in alphabetical order.
 *
 */

/**
 * Debounce functions.
 * - Calls a function when a user hasn't carried out an event for a specific
 *   amount of time.
 * @param {Function} callback The function to debounce.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function debounce(callback, wait, immediate) {
  let timeout = null

  return function(...args) {
    const callNow = immediate && !timeout

    function later() {
      timeout = null
      if (!immediate) {
        callback.apply(this, args)
      }
    }

    window.clearTimeout(timeout)
    timeout = window.setTimeout(later, wait)

    if (callNow) {
      callback.apply(this, args)
    }
  }
}

/**
 * Detects if a part of an element is in the viewport.
 * @param {HTMLElement} element - Element to check.
 * @returns {Boolean} - True if element is at least partially in viewport.
 */
export function isVisible(element) {
  const windowHeight = window.innerHeight

  const elementRectangle = element.getBoundingClientRect()
  const elementTop = elementRectangle.top
  const elementBottom = elementRectangle.bottom

  const topInView = (elementTop > 0) && (elementTop < windowHeight)
  const bottomInView = (elementBottom > 0) && (elementBottom < windowHeight)

  return (topInView || bottomInView)
}

/**
 * Scrolls page smoothly to element.
 * @param {HTMLElement} element - Element to scroll to.
 * @param {Object} data - Additional data.
 * @param {Function} data.callback - Function run after completion.
 * @param {HTMLElement} data.page - Element to scroll.
 */
export function scrollToElement(element, { callback, page = window } = {}) {
  let viewportFromPageTop = window.scrollY

  if (page !== window) {
    viewportFromPageTop = 0
  }

  const elementRectangle = element.getBoundingClientRect()
  const elementFromViewportTop = elementRectangle.top
  const position = (viewportFromPageTop + elementFromViewportTop)

  /**
   * Disable smooth scroll if prefers reduced motion.
   */
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth'

  page.scroll({
    behavior,
    left: 0,
    top: position,
  })

  if (typeof callback === 'function') {
    callback()
  }
}

/**
 * Set intersection observers to detect when add to cart button leaves viewport.
 * @param {Object} data - Data object.
 * @param {Object} data.callback - Function to run when intersecting.
 * @param {HTMLElement} data.element - Element to set observer on.
 * unobserved, must be a Vue element.
 * @param {String} [data.rootMargin] - This set of values serves to grow or shrink
 * each side of the root element's bounding box before computing intersections.
 * @param {Number|Array} [data.threshold] - Intersection observer threshold, 0.5
 * means the intersection is triggered when 50% of the element is in view.
 * @returns {Function}
 */
export function setIntersectionObserver({
  callback,
  element,
  rootMargin = '0px',
  threshold = 0.5,
}) {
  const options = {
    root: null,
    rootMargin,
    threshold,
  }

  const observer = new IntersectionObserver(callback, options)
  observer.observe(element)
}

/**
 * Throttle functions.
 * - Calls a function at intervals of a specified amount of time while the user
 *   is still carrying out the event.
 * @param {Function} callback - The function to execute when timer is passed.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function throttle(callback, wait, immediate = false) {
  let timeout = null
  let initialCall = true

  return function(...args) {
    const callNow = immediate && initialCall

    function next() {
      callback.apply(this, args)
      timeout = null
    }

    if (callNow) {
      initialCall = false
      next()
    }

    if (!timeout) {
      timeout = window.setTimeout(next, wait)
    }
  }
}

/**
 * Generates a unique identifier.
 * @returns {String}
 */
export function generateUUID() {
  let d = new Date().getTime()
  let d2 = (
    (typeof performance !== 'undefined') &&
    performance.now && (performance.now() * 1000)
  ) || 0

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    let r = Math.random() * 16
    if (d > 0) {
      r = (d + r) % 16 | 0
      d = Math.floor(d / 16)
    } else {
      r = (d2 + r) % 16 | 0
      d2 = Math.floor(d2 / 16)
    }
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
  })
}

/**
 * Checks if a string is valid JSON.
 * @param {string} string
 * @return {boolean}
 */
export function isValidJson(string) {
  try {
    JSON.parse(string)
  } catch (e) {
    return false
  }
  return true
}

/**
 * Wait for a window variable to be defined before proceeding with callback.
 * @example waitFor('nosto', callback)
 * @param {String} variable - Named window variable to wait for.
 * @param {Function} callback -Function to execute when interval is cleared.
 */
export function waitFor(variable, callback) {
  const timeout = 10000
  const loop = 500
  const start = performance.now()

  const interval = setInterval(() => {
    if (!window[variable]) {
      const difference = performance.now() - start

      if (difference > timeout) {
        window.console.error(`Error: ${variable} not loaded.`)
        clearInterval(interval)
      }

      return
    }

    clearInterval(interval)
    callback()
  }, loop)
}

/**
 * Returns the hidden sale price for the product if customer is eligible.
 * @param {Array} productTags - The product tags.
 * @param {String} currentCurrency - The current currency code.
 * @returns {String}
 */
export function getHiddenSalePrice(productTags, currentCurrency) {
  const saleCookieValue = `${encodeURIComponent('ls-can-view-hidden-sale')}=`
  const isEligible = document.cookie.includes(saleCookieValue)
  const currency = currentCurrency.toLowerCase() || "gbp"
  const priceTag = productTags?.find((tag) =>tag.startsWith(`vip-price-${currency}`))

  if (isEligible && priceTag) {
    return priceTag.split(':')[1].trim()
  }

  return ''
}

/**
 * Returns all hidden sale prices from product tags as an object with line item property format.
 * @param {Array} productTags - The product tags.
 * @returns {Object}
 */
export function getHiddenSalePrices(productTags) {
  const saleCookieValue = `${encodeURIComponent('ls-can-view-hidden-sale')}=`
  const isEligible = document.cookie.includes(saleCookieValue)

  if (!isEligible || !productTags?.length) {
    return {}
  }

  const hiddenSalePrices = {}

  productTags.forEach((tag) => {
    if (tag.startsWith('vip-price-')) {
      const parts = tag.split(':')
      if (parts.length >= 2) {
        const currency = parts[0].replace('vip-price-', '').trim()
        const price = parseFloat(parts[1].trim())

        if (currency && !isNaN(price)) {
          hiddenSalePrices[`_hidden_sale_price_${currency}`] = price
        }
      }
    }
  })

  return hiddenSalePrices
}

/**
 * Returns the hash of passed data (using algorithm 'SHA-1').
 * @param {Object} data - data to be hashed.
 * @returns {String} Hex presentation of data hash.
 */
export async function hash(data) {
  // encode as (utf-8) Uint8Array
  const msgUint8 = new TextEncoder().encode(data)
  // hash the data
  const hashBuffer = await crypto.subtle.digest("SHA-1", msgUint8)
  // convert buffer to byte array
  const hashArray = Array.from(new Uint8Array(hashBuffer))

  // convert bytes to hex string
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")

  return hashHex
}

/**
 * Checks if an element is overflowing.
 * @param {HTMLElement} element - The element to check.
 * @return {boolean}
 */
export function checkOverflow(element) {
  const currentOverflow = element.style.overflow

  if (!currentOverflow || currentOverflow === 'visible') {
    element.style.overflow = 'hidden'
  }

  const isOverflowing = element.clientWidth < element.scrollWidth
    || element.clientHeight < element.scrollHeight

  element.style.overflow = currentOverflow

  return isOverflowing
}
